import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, ShieldCheck, ShoppingBag, PackageCheck, Heart } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../core/api.jsx";
import { sendLoginOtp, verifyLoginOtp } from "../../core/services/authService";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import storeLogo from "../../assets/images/logo.png";

export default function Login() {
  const location = useLocation();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(
    location.state?.otpPending ? "otp" : "phone",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { isAuthenticated, setSession } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const returnTo = location.state?.from || "/profile";

  useEffect(() => {
    if (location.state?.phone) setPhone(location.state.phone);
    if (location.state?.otpPending) setStep("otp");
  }, [location.state]);
  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  const requestOtp = async (event) => {
    event.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10)
      return setError("Enter a valid 10-digit mobile number.");
    setBusy(true);
    setError("");
    try {
      const data = await sendLoginOtp(digits);
      setPhone(digits);
      if (data.isNewUser) {
        navigate("/register", {
          state: { phone: digits, otpPending: true, from: returnTo },
        });
        return;
      }
      setStep("otp");
    } catch (requestError) {
      setError(getErrorMessage(requestError, "We couldn't send your code."));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (otp.trim().length !== 6)
      return setError("Enter the 6-digit code we sent you.");
    setBusy(true);
    setError("");
    try {
      const data = await verifyLoginOtp({ phone, otp: otp.trim() });
      setSession(data.token, data.user);
      notify(
        `Welcome${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : " back"}!`,
      );
      navigate(returnTo, { replace: true });
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "That code didn't work. Try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page page-shell">
      <section className="auth-layout auth-layout--login">
        <aside className="auth-story">
          <div className="auth-story__top"><img src={storeLogo} alt="Sugam Store" /><span><ShieldCheck size={15} /> Secure account</span></div>
          <div className="auth-story__content">
            <span className="auth-story__icon"><ShoppingBag size={25} /></span>
            <p className="auth-story__kicker">YOUR EVERYDAY STORE</p>
            <h2>Good finds.<br />A smoother checkout.</h2>
            <p>Sign in to pick up where you left off and keep all your orders in one place.</p>
            <div className="auth-story__perks"><span><PackageCheck size={17} /> Track every order</span><span><Heart size={17} /> Keep favourites close</span></div>
          </div>
          <span className="auth-story__foot">Thoughtful shopping, made simple.</span>
        </aside>
        <div className="auth-card">
        <div className="auth-card__brand">
          <img src={storeLogo} alt="Sugam Store" />
          <span className="auth-card__seal">
            <ShieldCheck size={21} />
          </span>
        </div>
        {step === "phone" ? (
          <>
            <p className="eyebrow">Welcome back</p>
            <h1>
              Sign in to
              <br />
              your account.
            </h1>
            <p className="auth-card__copy">
              Enter your mobile number and we’ll send you a one-time
              verification code.
            </p>
            <form className="form-stack" onSubmit={requestOtp}>
              <label className="field-label" htmlFor="login-phone">
                Mobile number
              </label>
              <div className="phone-field">
                <span>+91</span>
                <input
                  id="login-phone"
                  inputMode="tel"
                  autoComplete="tel-national"
                  maxLength={12}
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
              {error && <p className="form-error">{error}</p>}
              <button
                className="button button--primary button--full"
                disabled={busy}
              >
                {busy ? (
                  "Sending code…"
                ) : (
                  <>
                    Send sign-in code <ArrowRight size={17} />
                  </>
                )}
              </button>
            </form>
            <p className="auth-card__switch">
              New to Sugam Store?{" "}
              <Link to="/register" state={{ from: returnTo }}>
                Create an account
              </Link>
            </p>
          </>
        ) : (
          <>
            <button
              className="text-button auth-back"
              onClick={() => {
                setStep("phone");
                setError("");
              }}
            >
              <ArrowLeft size={15} /> Change number
            </button>
            <p className="eyebrow">One last step</p>
            <h1>
              Check your
              <br />
              messages.
            </h1>
            <p className="auth-card__copy">
              We sent a 6-digit code to <strong>+91 {phone}</strong>.
            </p>
            <form className="form-stack" onSubmit={verifyOtp}>
              <label className="field-label" htmlFor="login-otp">
                Verification code
              </label>
              <input
                className="text-field otp-field"
                id="login-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="• • • • • •"
                value={otp}
                onChange={(event) =>
                  setOtp(event.target.value.replace(/\D/g, ""))
                }
              />
              {error && <p className="form-error">{error}</p>}
              <button
                className="button button--primary button--full"
                disabled={busy}
              >
                {busy ? (
                  "Verifying…"
                ) : (
                  <>
                    Verify & sign in <Check size={17} />
                  </>
                )}
              </button>
            </form>
            <button
              className="text-button auth-resend"
              disabled={busy}
              onClick={requestOtp}
            >
              Didn't receive a code? Send again
            </button>
          </>
        )}
        <Link to="/shop" className="auth-card__back-shop">
          <ArrowLeft size={14} /> Continue shopping
        </Link>
        </div>
      </section>
    </main>
  );
}
